import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import apis from '@src/apis';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  KeyboardArrowDownRounded,
  SearchRounded,
} from '@mui/icons-material';
import {
  MAX_LENGTH_USERNAME,
  MAX_LENGTH_VOICE_NAME,
  MIN_LENGTH_USERNAME,
  USERNAME_EXISTS,
  VOICE_LOCALES,
  VOICE_STATUS,
} from '@src/constants/voiceCloning';
import actions from '@src/redux/actions';
import { LoadingButton } from '@mui/lab';
import { uploadFileToS3 } from '@src/services/upload';
import { GENDER } from '@src/constants/voice';
import { nonAccentVietnamese } from '@src/utils/accent';
import { REGEX_SPECIAL_CHARACTERS } from '@src/utils/checkValid';
import {
  StyledEditVoiceInformation,
  StyledProvincesListDesktop,
  StyledProvincesListMobile,
} from './index.style';

const initialFilter = { search: '', locale: '' };

const ProvincesList = ({
  filter,
  provinces,
  openProvinces,
  handleChooseProvince,
  handleCloseProvinces,
  handleChangeSearchFilter,
}) => {
  const { t } = useTranslation();
  return (
    <StyledProvincesListMobile
      open={openProvinces}
      onClose={handleCloseProvinces}
    >
      <Box className="box-dialog">
        <Box className="box-search">
          <TextField
            className="search"
            value={filter.search}
            name="search"
            size="small"
            fullWidth
            placeholder={t('searchVoicePlaceholder')}
            onChange={handleChangeSearchFilter}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box className="provinces">
          {provinces.map((item, index) => (
            <Button
              className={`province ${index === 0 ? 'first' : ''} ${
                index === provinces.length - 1 ? 'last' : ''
              }`}
              key={item.id}
              onClick={() => handleChooseProvince(item)}
            >
              {t(item.name)}
            </Button>
          ))}
        </Box>
      </Box>
    </StyledProvincesListMobile>
  );
};

const ProvincesListDesktop = ({
  filter,
  provinces,
  openProvinces,
  handleChooseProvince,
  handleCloseProvinces,
  handleChangeSearchFilter,
}) => {
  const { t } = useTranslation();

  const handleClickProvince = (item) => {
    handleChooseProvince(item);
    handleCloseProvinces();
  };

  if (!openProvinces) return null;

  return (
    <ClickAwayListener onClickAway={handleCloseProvinces}>
      <StyledProvincesListDesktop>
        <TextField
          className="search"
          value={filter.search}
          name="search"
          size="small"
          fullWidth
          placeholder={t('searchVoicePlaceholder')}
          onChange={handleChangeSearchFilter}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded />
              </InputAdornment>
            ),
          }}
        />
        <Box className="provinces">
          {provinces.map((item) => (
            <Button
              key={item?.name}
              className="province"
              onClick={() => handleClickProvince(item)}
            >
              {t(item.name)}
            </Button>
          ))}
        </Box>
      </StyledProvincesListDesktop>
    </ClickAwayListener>
  );
};

const AvatarEdit = ({ avatar, handleAvatarChange }) => (
  <Box className="avatar-wrapper">
    <label htmlFor="avatar-input">
      <Avatar src={avatar} alt="avatar" className="avatar" />
      <input
        type="file"
        accept="image/*"
        id="avatar-input"
        style={{ display: 'none' }}
        onChange={handleAvatarChange}
      />
      <Box className="edit-icon">
        <Edit className="icon" />
      </Box>
    </label>
  </Box>
);

const EditVoiceInformation = ({ updateVoice, goBack }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Redux
  const { voice } = useSelector((state) => state.voiceVoiceCloning);
  const { provinces: provincesStore } = useSelector((state) => state.province);

  // State
  const [isEdit, setIsEdit] = useState(false);
  const [isEditUserName, setIsEditUserName] = useState(false);
  const [voiceName, setVoiceName] = useState(voice.name);
  const [avatar, setAvatar] = useState(voice.avatar);
  const [gender, setGender] = useState(voice.gender);
  const [userName, setUserName] = useState(voice.username);
  const [voiceLocale, setVoiceLocale] = useState(voice.locale);
  const [voiceProvince, setVoiceProvince] = useState(voice.province);
  const [filter, setFilter] = useState({
    ...initialFilter,
    locale: voice.locale,
  });
  const [provinces, setProvinces] = useState([]);
  const [provincesLoading, setProvincesLoading] = useState(false);
  const [openProvinces, setOpenProvinces] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);
  const [disableSaveButton, setDisableSaveButton] = useState(true);
  const [loading, setLoading] = useState(false);

  // Constants
  const isVoiceRecording = voice?.status === VOICE_STATUS.RECORDING;
  const hasDataChanged =
    userName !== voice.username ||
    voiceName !== voice.name ||
    gender !== voice.gender ||
    voiceLocale !== voice.locale ||
    voiceProvince?.id !== voice.province?.id;

  const handleGoBack = () => {
    setOpenProvinces(false);
    goBack();
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
    setDisableSaveButton(false);
  };

  const handleChangeGender = (value) => setGender(value);

  const handleChangeLocaleFilter = (value) => {
    if (value !== voiceLocale) setVoiceProvince({});
    setVoiceLocale(value);
    setFilter({ ...filter, locale: value });
  };

  const handleOpenProvinces = () => setOpenProvinces(true);

  const handleCloseProvinces = () => setOpenProvinces(false);

  const handleChangeSearchFilter = (e) => {
    const { name, value } = e.target;
    setFilter({ ...filter, [name]: value });
  };

  const handleChooseProvince = (item) => {
    setVoiceProvince(item);
    setOpenProvinces(false);
  };

  const handleChangeVoiceName = (e) => setVoiceName(e.target.value);

  const handleChangeUserName = (e) => {
    const cleanedName = nonAccentVietnamese(e.target.value)
      .replace(REGEX_SPECIAL_CHARACTERS, '')
      .toLowerCase();
    setUserName(cleanedName.replace(' ', ''));
  };

  const handleStartEdit = () => setIsEdit(true);

  const handleStartEditUserName = () => setIsEditUserName(true);

  const showErrorNotification = (message, params = {}) =>
    dispatch(
      actions.noti.push({
        severity: 'error',
        message: t(message, params),
      }),
    );

  const showSuccessNotification = (message) =>
    dispatch(
      actions.noti.push({
        severity: 'success',
        message: t(message),
      }),
    );

  const renderStyleLocale = (item) => {
    switch (item) {
      case VOICE_LOCALES.NORTHERN:
        return 'northern';
      case VOICE_LOCALES.CENTRAL:
        return 'central';
      case VOICE_LOCALES.SOUTHERN:
        return 'southern';
      default:
        return '';
    }
  };

  const handleUploadAvatarToS3 = async (fileName, file) => {
    const extension = fileName.split('.')[1];
    const name = fileName.split('.')[0];
    const directory = 'images/voice-cloning';
    const s3Url = await uploadFileToS3(name, directory, extension, file);

    if (!s3Url) {
      showErrorNotification('uploadError');
      return null;
    }

    return s3Url;
  };

  const uploadImageToS3 = async (file) => {
    const avatarName = file.name;
    const ext = avatarName.split('.').pop();
    const imgName = avatarName.slice(0, avatarName.lastIndexOf('.'));
    const link = await handleUploadAvatarToS3(
      `${imgName}_${uuid()}.${ext}`,
      avatarFile,
    );
    return link;
  };

  const verifyChangedData = () => {
    if (voiceName.length > MAX_LENGTH_VOICE_NAME) {
      showErrorNotification('errorVoiceNameLength', {
        max: MAX_LENGTH_VOICE_NAME,
      });
      return false;
    }

    if (voiceName.trim() === '') {
      showErrorNotification('voiceNameIsRequired');
      return false;
    }

    if (userName.trim() === '') {
      showErrorNotification('voiceCodeIsRequired');
      return false;
    }

    if (
      userName.trim().length < MIN_LENGTH_USERNAME ||
      userName.trim().length > MAX_LENGTH_USERNAME
    ) {
      showErrorNotification('errorUsernameLength', {
        min: MIN_LENGTH_USERNAME,
        max: MAX_LENGTH_USERNAME,
      });
      return false;
    }

    if (Object.keys(voiceProvince).length === 0) {
      showErrorNotification('provinceIsRequired');
      return false;
    }

    return true;
  };

  const handleSaveChange = async () => {
    const isValid = verifyChangedData();
    if (!isValid) return;

    setLoading(true);
    let avatarUrl = voice.avatar;
    if (avatarFile) {
      avatarUrl = await uploadImageToS3(avatarFile);
      if (!avatarUrl) {
        showErrorNotification('createVoiceFailed');
        setLoading(false);
        return;
      }
    }
    setDisableSaveButton(true);

    const dataUpdated = { name: voiceName, avatar: avatarUrl };

    if (gender !== voice.gender) dataUpdated.gender = gender;
    if (voiceLocale !== voice.locale) dataUpdated.locale = voiceLocale;
    if (voiceProvince?.id !== voice.province?.id)
      dataUpdated.provinceId = voiceProvince?.id;
    if (userName !== voice.username) dataUpdated.username = userName;

    const response = await apis.voiceCloning.updateVoiceCloningVoice(
      voice.id,
      dataUpdated,
    );
    if (!response?.status) {
      if (response.errorCode === USERNAME_EXISTS)
        showErrorNotification('voiceUsernameExistsError');
      else showErrorNotification('createVoiceFailed');
      setLoading(false);
      return;
    }

    const updatedVoice = {
      ...voice,
      name: voiceName,
      avatar: avatarUrl,
      ...dataUpdated,
      province: voiceProvince,
    };

    updateVoice(updatedVoice);
    dispatch(actions.voiceVoiceCloning.addVoice(updatedVoice));
    setIsEdit(false);
    setIsEditUserName(false);
    showSuccessNotification('updateVoiceInformationSuccess');
    setLoading(false);
  };

  const fetchProvinces = async () => {
    setProvincesLoading(true);
    const response = await apis.voiceCloning.getVoiceCloningProvinces({
      searchFields: 'name,code,locale',
    });

    if (!response?.status) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'getProvincesFailed',
        }),
      );
      setProvincesLoading(false);
      return;
    }
    // Sort provinces by locale and name
    const sortProvinces = response?.result?.provinces.sort((a, b) => {
      if (a.locale === b.locale) return a.name.localeCompare(b.name);
      return a.locale.localeCompare(b.locale);
    });
    dispatch(actions.province.addProvinces(sortProvinces));
    setProvincesLoading(false);
  };

  useEffect(() => {
    if (!provincesStore.length) fetchProvinces();
  }, []);

  useEffect(() => {
    if (provincesStore.length) {
      // Filter provinces by locale
      const filteredProvincesByLocale = provincesStore.filter((item) =>
        item.locale.toLowerCase().includes(filter.locale.toLowerCase()),
      );
      // Filter provinces by name or code
      const filteredProvincesBySearch = filteredProvincesByLocale.filter(
        (item) =>
          item.name.toLowerCase().includes(filter.search.toLowerCase()) ||
          item.code.toLowerCase().includes(filter.search.toLowerCase()),
      );
      setProvinces(filteredProvincesBySearch);
    }
  }, [filter, provincesStore]);

  const EditTitle = () => (
    <Box className="edit-title-wrapper">
      <ArrowBack className="icon" onClick={handleGoBack} />
      <Typography className="edit-title">{t('voiceInformation')}</Typography>
      <Box />
    </Box>
  );

  const Information = () => (
    <Box className="info-wrapper">
      {/* Voice Name */}
      <Box className="info-field-wrapper">
        <Typography className="field-title">{t('voiceName')}</Typography>
        {!isEdit ? (
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            onClick={handleStartEdit}
          >
            <Typography className="field-value">{voiceName}</Typography>
            <Edit className="icon-edit" />
          </Box>
        ) : (
          <TextField
            className="text-field"
            variant="outlined"
            value={voiceName}
            onChange={handleChangeVoiceName}
            autoFocus
          />
        )}
      </Box>

      {/* Voice Code */}
      {!isVoiceRecording ? (
        <Box className="info-field-wrapper">
          <Typography className="field-title">{t('username')}</Typography>
          <Typography className="field-value">{userName}</Typography>
        </Box>
      ) : (
        <Box className="info-field-wrapper">
          <Typography className="field-title">{t('username')}</Typography>
          {!isEditUserName ? (
            <Box
              display="flex"
              alignItems="center"
              gap="8px"
              onClick={handleStartEditUserName}
            >
              <Typography className="field-value">{userName}</Typography>
              <Edit className="icon-edit" />
            </Box>
          ) : (
            <TextField
              className="text-field"
              variant="outlined"
              value={userName}
              onChange={handleChangeUserName}
              autoFocus
            />
          )}
        </Box>
      )}

      {/* Gender */}
      {!isVoiceRecording ? (
        <Box className="info-field-wrapper">
          <Typography className="field-title">{t('gender')}</Typography>
          <Typography className="field-value">{t(gender)}</Typography>
        </Box>
      ) : (
        <Box className="info-field-wrapper select-box">
          <Typography className="field-title">{t('gender')}</Typography>
          <Box className="gender-box-button">
            {GENDER.map((option, index) => (
              <Button
                key={option.label}
                className={`gender-button ${
                  index === 0 ? 'button-right' : 'button-left'
                } ${gender === option.value ? 'selected' : ''}`}
                onClick={() => handleChangeGender(option.value)}
              >
                {t(option.label)}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Locale */}
      {!isVoiceRecording ? (
        <Box className="info-field-wrapper">
          <Typography className="field-title">{t('onlyVoice')}</Typography>
          <Typography className="field-value">{t(voiceLocale)}</Typography>
        </Box>
      ) : (
        <Box className="info-field-wrapper select-box">
          <Typography className="field-title">{t('onlyVoice')}</Typography>
          <Box className="locale-box-button">
            {Object.values(VOICE_LOCALES).map((value) => (
              <Button
                key={value}
                className={`locale-button ${renderStyleLocale(value)} ${
                  voiceLocale === value ? 'selected' : ''
                }`}
                onClick={() => handleChangeLocaleFilter(value)}
              >
                {t(value)}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Province name */}
      {!isVoiceRecording ? (
        <Box className="info-field-wrapper">
          <Typography className="field-title">{t('voiceProvince')}</Typography>
          <Typography className="field-value">{voiceProvince?.name}</Typography>
        </Box>
      ) : (
        <Box className="info-field-wrapper select-box" position="relative">
          <Typography className="field-title">{t('voiceProvince')}</Typography>
          <Button
            onClick={handleOpenProvinces}
            className="province-button"
            disabled={!voiceLocale}
          >
            {!voiceProvince?.name ? (
              <Typography className={`${!voiceLocale ? 'disabled' : 'select'}`}>
                {t('selectProvince')}
              </Typography>
            ) : (
              <Typography className="province">
                {t(voiceProvince?.name)}
              </Typography>
            )}
            {provincesLoading ? (
              <Box className="loading">
                <CircularProgress size={15} />
              </Box>
            ) : (
              <KeyboardArrowDownRounded className="icon" />
            )}
          </Button>
          {!isMobile && (
            <ProvincesListDesktop
              filter={filter}
              provinces={provinces}
              openProvinces={openProvinces}
              handleChooseProvince={handleChooseProvince}
              handleCloseProvinces={handleCloseProvinces}
              handleChangeSearchFilter={handleChangeSearchFilter}
            />
          )}
        </Box>
      )}
    </Box>
  );

  const SaveChangeButton = () => (
    <LoadingButton
      variant="contained"
      fullWidth
      className="save-button"
      disabled={disableSaveButton && !hasDataChanged}
      onClick={handleSaveChange}
      loading={loading}
    >
      {loading ? '' : t('saveChange')}
    </LoadingButton>
  );

  return (
    <StyledEditVoiceInformation>
      <EditTitle />
      <AvatarEdit avatar={avatar} handleAvatarChange={handleAvatarChange} />
      <Information />
      <SaveChangeButton />
      {isMobile && (
        <ProvincesList
          filter={filter}
          provinces={provinces}
          openProvinces={openProvinces}
          handleChooseProvince={handleChooseProvince}
          handleCloseProvinces={handleCloseProvinces}
          handleChangeSearchFilter={handleChangeSearchFilter}
        />
      )}
    </StyledEditVoiceInformation>
  );
};

export default EditVoiceInformation;
